<script>
export default {
  name: 'BaseStatus',
  props: {
    text: { type: Boolean, default: false },
    // Chip suave — fundo no tom clarinho da cor + bolinha (sem ícone).
    soft: { type: Boolean, default: false },
    content: { type: String, default: 'Status' },
    color: { type: String, default: '' },
    icon: { type: String, default: '' },
    useMapIcon: { type: Boolean, default: false },
    defaultIcon: { type: String, default: 'mdi-circle' },
    defaultColor: { type: String, default: '#7C89B7' },
    preserveCase: { type: Boolean, default: false },
  },
  data() {
    return {
      // Aliases de cores semânticas que NÃO são tokens reais do projeto, mapeados
      // para o token equivalente existente. Ex.: 'error' é o nome semântico comum,
      // mas o token de erro no DS é 'danger' (--danger). Sem isso, color="error"
      // resolve para var(--error) — inexistente — e o chip soft fica sem fundo/cor,
      // parecendo texto solto. Um color explícito deve SEMPRE gerar um chip visível.
      colorAlias: {
        error: 'danger',
      },
      iconMap: {
        pendente: 'info-circle',
        criado: 'info-circle',
        concluido: 'check-circle',
        concluído: 'check-circle',
        executada: 'check-circle',
        'executada parcial': 'progress-check',
        cancelado: 'close-circle',
        cancelada: 'close-circle',
        expirado: 'close-circle',
        compra: 'mdi-upload',
        venda: 'mdi-download',
        troca: 'mdi-swap-horizontal',
        // next..
      },
      colorMap: {
        pendente: 'warning-new',
        criado: '#8E8E8E',
        concluido: '#56AF79',
        concluído: '#56AF79',
        executada: '#56AF79',
        'executada parcial': '#F39C12',
        cancelado: 'danger',
        cancelada: 'danger',
        expirado: 'danger',
        compra: '#56AF79',
        venda: '#E74C3C',
        troca: '#3498DB',
        // next..
      },
    }
  },
  computed: {
    normalizedContent() {
      return this.content ? this.content.toLowerCase().trim() : ''
    },
    displayContent() {
      if (!this.content) return ''
      return this.preserveCase ? this.content.trim() : this.normalizedContent
    },
    colorFormatted() {
      return this.formatColor(this.color)
    },
    resolvedColor() {
      if (this.color) return this.color
      if (this.useMapIcon && this.colorMap[this.normalizedContent]) {
        return this.colorMap[this.normalizedContent]
      }
      return this.defaultColor
    },
    cssColor() {
      return this.formatColor(this.resolvedColor)
    },
    computedIcon() {
      if (this.icon && this.icon !== '') {
        return this.icon
      }
      if (this.useMapIcon && this.iconMap[this.normalizedContent]) {
        return this.iconMap[this.normalizedContent]
      }
      return this.defaultIcon
    },
    computedColor() {
      if (this.color) return this.text ? this.colorFormatted : this.color
      if (this.useMapIcon && this.colorMap[this.normalizedContent]) {
        const mappedColor = this.colorMap[this.normalizedContent]
        return this.text ? this.formatColor(mappedColor) : mappedColor
      }
      return this.defaultColor
    },
    hasIcon() {
      return this.computedIcon !== ''
    },
    hasIconForText() {
      return this.text && !!this.icon
    },
    statusLabelClass() {
      return this.hasIcon ? 'padding-with-icon' : 'padding-without-icon'
    },
  },
  methods: {
    formatColor(color) {
      if (color.includes('#')) return color
      const normalized = this.colorAlias[color] || color
      return `var(--${normalized})`
    },
  },
}
</script>

<template>
  <div>
    <!-- Status estilo texto -->
    <template v-if="text">
      <div class="d-flex align-center">
        <icon-svg
          v-if="hasIconForText"
          class="mr-1"
          :name="icon"
          size="14"
          :color="computedColor"
        />
        <div
          v-else
          class="status-dot mr-1"
          :style="{ backgroundColor: computedColor }"
        />
        <span
          class="status-text"
          :class="{ 'status-text--preserve-case': preserveCase }"
          :style="{ color: computedColor }"
        >
          {{ displayContent }}
        </span>
      </div>
    </template>

    <!-- Status estilo chip suave (bolinha + fundo clarinho da cor) -->
    <template v-else-if="soft">
      <div
        class="status-chip status-chip--soft d-inline-flex align-center"
        :style="{ '--status-color': cssColor }"
      >
        <div class="status-dot status-dot--soft mr-1" />
        <!-- `displayContent` + a classe de preserve-case, igual aos outros dois
             ramos: o `soft` era o único que ignorava a prop `preserveCase` que
             o componente declara — renderizava `normalizedContent` e não
             aplicava a classe. Resultado: qualquer rótulo PT-BR em modo soft saía
             title-case ("Acima Do Risco", "Dentro Do Risco"). 17 call-sites em
             6 módulos já passavam `soft preserve-case` e não recebiam nada. -->
        <span
          class="status-label status-label--soft"
          :class="{ 'status-label--preserve-case': preserveCase }"
        >
          {{ displayContent }}
        </span>
      </div>
    </template>

    <!-- Status estilo chip (com ícone) -->
    <template v-else>
      <div class="status-chip d-inline-flex align-center">
        <icon-svg
          v-if="hasIcon"
          class="mr-1"
          :name="computedIcon"
          size="15"
          :color="computedColor"
        />
        <span
          :class="[
            statusLabelClass,
            'status-label',
            { 'status-label--preserve-case': preserveCase },
          ]"
        >
          {{ displayContent }}
        </span>
      </div>
    </template>
  </div>
</template>

<style lang="sass" scoped>
.status-dot
  width: 8px
  height: 8px
  border-radius: 50%
  // Nunca encolhe no flex (senão a bolinha vira elipse quando o texto trunca).
  flex-shrink: 0

.status-dot--soft
  background-color: var(--status-color)

.status-text
  font-family: 'Archivo', sans-serif
  font-size: 12px
  font-weight: 500
  text-transform: capitalize
  &--preserve-case
    text-transform: none

.status-chip
  background-color: #F1F1F1
  height: 20px
  padding: 4px
  border-radius: 20px
  font-family: 'Archivo', sans-serif
  font-size: 12px
  font-weight: 600
  color: #5E5E5E

// Chip suave — fundo é a cor enviada, porém clarinho (tint via ::before)
.status-chip--soft
  position: relative
  isolation: isolate
  background-color: transparent
  padding: 4px 8px
  // Em espaços pequenos, respeita a largura do container e trunca o texto
  // (via .status-label--soft) em vez de quebrar em duas linhas.
  max-width: 100%

  &::before
    content: ''
    position: absolute
    inset: 0
    background-color: var(--status-color)
    opacity: 0.16
    border-radius: inherit
    z-index: -1

.status-label
  font-family: 'Geist', sans-serif
  font-size: 12px
  font-weight: 600
  color: #5E5E5E
  line-height: 20px
  text-transform: capitalize
  &--preserve-case
    text-transform: none

.status-label--soft
  color: var(--status-color)

.status-label--soft
  // Texto no tom legivel (DS): cores canonicas usam o tom exato do guia
  // (--status-text-color); demais sao escurecidas via color-mix p/ contraste no
  // fundo tint. Fallback (browsers sem color-mix): a cor cheia.
  color: var(--status-color)
  color: var(--status-text-color, color-mix(in srgb, var(--status-color), #000 30%))
  // Trunca com reticências em vez de quebrar linha (min-width:0 permite o flex
  // item encolher abaixo do conteúdo).
  min-width: 0
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.padding-with-icon
  padding-right: 6px

.padding-without-icon
  padding: 0 6px
</style>
